import * as React from 'react';
import { EcommProductPrice } from '@arctheme-components/ecommerce/Price/Price';
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import useMediaQuery from '../../../../hooks/useMediaQuery';
import useCurrentTextStyle from '../../../../hooks/useCurrentTextStyle';
import Alpine from 'alpinejs';
import helpers from '../../../../../js/helpers';
import { useEffect, useRef, useState } from 'react';
import useCurrency from '@src/hooks/useCurrency';

export const ProductPrice = ({ product, isMobile, settings, globals }) => {
  const [currencyCode, setCurrencyCode] = useState((window as any).currency?.name ?? 'AUD');
  const staticCurrencySymbol = (window as any).store_currency_symbol || '$';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { getCurrentTextStyle } = useCurrentTextStyle();
  const { currentCurrencySymbol } = useCurrency();
  const currencySymbolDisplay = staticCurrencySymbol || currentCurrencySymbol;
  // Get mobile state for responsive logic
  const isMobileQuery = useMediaQuery(null, 767);

  const {
    classes,
    render_type,
    display_direction,
    display_direction_mobile,
    gap,
    gap_mobile,
    show_compare_at,
    show_country_code,
    show_price_cents,
    show_sale_price_cents,
    show_compare_at_price_cents,
    price_style,
    price_style_mobile,
    price_style_tablet,
    price_style_ultrawide,
    price_style_enable_responsive,
    price_colour,
    sale_price_style,
    sale_price_style_mobile,
    sale_price_style_tablet,
    sale_price_style_ultrawide,
    sale_price_style_enable_responsive,
    sale_price_colour,
    compare_at_price_style,
    compare_at_price_style_mobile,
    compare_at_price_style_tablet,
    compare_at_price_style_ultrawide,
    compare_at_price_style_enable_responsive,
    compare_at_price_colour,
    strike_through_compare_at,
    padding_top,
    padding_right,
    padding_bottom,
    padding_left,
    margin_top,
    margin_right,
    margin_bottom,
    margin_left
  } = settings;



  useEffect(() => {
    window.addEventListener('currency_initialised', (event: any) => {
      setCurrencyCode(event.detail.currency?.name);
    });
  }, []);

  // Helper function to get price by currency code
  const getPriceByCurrency = (priceRanges, currency, type = 'price') => {
    const priceEntry = priceRanges?.find((item) => item[type]?.currency_code === currency);
    return priceEntry?.[type]?.amount || 0;
  };

  // Helper function to format price with or without cents
  const formatPrice = (priceInCents, showCents) => {
    const price = priceInCents / 100; // Convert cents to dollars
    const decimalPlaces = showCents ? 2 : 0;
    return price.toFixed(decimalPlaces);
  };

  // Default prices from compare_at_price and price (keep as cents)
  let minPrice = product?.price > 0 ? product?.price : 0;
  let maxPrice = product?.compare_at_price > 0 ? product?.compare_at_price : 0;

  // If presentment_price_ranges exist, extract prices for the current currency
  if (product?.presentment_price_ranges) {
    const presentmentMinPrice = getPriceByCurrency(product?.variants[0]?.presentment_prices, currencyCode, 'price');
    const presentmentMaxPrice = getPriceByCurrency(
      product?.variants[0]?.presentment_prices,
      currencyCode,
      'compare_at_price'
    );

    // Use presentment prices only if they are available (keep in cents)
    minPrice = presentmentMinPrice ?? minPrice;
    maxPrice = presentmentMaxPrice ?? maxPrice;
  }

  product?.variants?.map((variant) => {
    const variantComparePrice = variant.compare_at_price;
    if (variantComparePrice > 0 && variantComparePrice > maxPrice) {
      // Keep price in cents for consistency
      maxPrice = variantComparePrice;
    }
  });

  // Ensure that compare_at_price is treated as a "strike-through price" if higher
  const isOnSale = minPrice > 0 && minPrice < maxPrice;

  // Handle render type visibility
  const shouldRender = () => {
    if (render_type === 'mobile-only' && !isMobileQuery) return false;
    if (render_type === 'desktop-only' && isMobileQuery) return false;
    return true;
  };

  // Don't render if render type doesn't match current device
  if (!shouldRender()) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={`product-tile__price flex justify-start
                ${classes}
                pt-[${padding_top}px]
                pb-[${padding_bottom}px]
                pl-[${padding_left}px]
                pr-[${padding_right}px]
                mt-[${margin_top}px]
                mb-[${margin_bottom}px]
                ml-[${margin_left}px]
                mr-[${margin_right}px]
            `}
    >
      <div
        className={`flex ${
          isMobileQuery
            ? `${display_direction_mobile} ${display_direction_mobile.includes('row') ? 'items-center' : ''}`
            : `${display_direction} ${display_direction.includes('row') ? 'items-center' : ''}`
        } gap-[${isMobileQuery ? gap_mobile : gap}px]`}
      >
        {isOnSale && (
          <span
            className={getCurrentTextStyle(sale_price_style, sale_price_style_enable_responsive, sale_price_style_mobile, sale_price_style_tablet, sale_price_style_ultrawide)}
            style={{ color: sale_price_colour }}
          >
            <div
              className='product__price'
              data-prodify-price-container=''
            >
              {currencySymbolDisplay}
              {formatPrice(minPrice, show_sale_price_cents)} {show_country_code ? `${currencyCode}` : ''}
            </div>
          </span>
        )}
        <div
          className={`product__price`}
          data-prodify-price-container=''
        >
          {isOnSale && show_compare_at ? (
            <>
              <span className='sr-only'>Special Price</span>
              <div
                className={`product__sale-price ${getCurrentTextStyle(compare_at_price_style, compare_at_price_style_enable_responsive, compare_at_price_style_mobile, compare_at_price_style_tablet, compare_at_price_style_ultrawide)} ${
                  strike_through_compare_at ? 'line-through' : ''
                }`}
                style={{ color: compare_at_price_colour }}
              >
                {currencySymbolDisplay}
                {formatPrice(maxPrice, show_compare_at_price_cents)} {show_country_code ? `${currencyCode}` : ''}
              </div>
            </>
          ) : (
            <>
              <span className='sr-only'>Regular Price</span>
              <div
                className={`product__price ${getCurrentTextStyle(price_style, price_style_enable_responsive, price_style_mobile, price_style_tablet, price_style_ultrawide)}`}
                style={{ color: price_colour }}
              >
                {currencySymbolDisplay}
                {formatPrice(minPrice, show_price_cents)} {show_country_code ? `${currencyCode}` : ''}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
